import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCustomerShareLink, useRevokeCustomerShare } from '../../../../api/customer';
import { copyText } from '../../../../utils/clipboard';

interface Props {
  customerId: string;
  customerName?: string;
  onClose: () => void;
}

/**
 * 客户订单核对链接的生成/撤销弹窗。
 *
 * 与订单分享弹窗（`orders/components/ShareOrderModal.tsx`）的差别只有两件事：
 * 这一种分享**只发得出链接**、没有文字摘要（十三列的台账粘进微信不可读，那一页的用途本来就是打开链接看表），
 * 以及核对单只有一批欠款单（2026-10-11 用户裁定：未付款与部分付款合并，不再分「未付清 / 仅未付款」两档）。
 * 数据一律不落快照：链接打开时才按分享人的可见范围与审核闸门实时取数，
 * 所以这里只给一句「以打开那一刻的订单为准」，不预览条数与金额（预览了也不算数）。
 */
export default function ShareCustomerModal({ customerId, customerName, onClose }: Props) {
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState(false);
  const [revoked, setRevoked] = useState(false);
  const revokedRef = useRef(false);
  const shareLink = useCustomerShareLink(customerId);
  const revokeShare = useRevokeCustomerShare();
  const share = shareLink.data;

  // 撤销后关掉的弹窗必须把这条作废链接从缓存里摘掉：query 的 staleTime 是 Infinity
  // （否则每次切回页面都会重新生成、把上一条已发出去的链接作废），留着它下次打开就是
  // 一条看着有效、点开却「链接无效」的死链接。摘掉后下次打开会重新 POST 一条新的。
  useEffect(
    () => () => {
      if (revokedRef.current) queryClient.removeQueries({ queryKey: ['customer-share', customerId] });
    },
    [customerId, queryClient],
  );

  const handleCopy = async () => {
    if (!share) return;
    try {
      await copyText(share.shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      alert(e instanceof Error && e.message ? e.message : '复制失败，请手动选中复制');
    }
  };

  const handleRevoke = async () => {
    try {
      await revokeShare.mutateAsync(customerId);
      revokedRef.current = true;
      setRevoked(true);
    } catch (e) {
      // 撤销失败却提示成功，用户会以为公开入口已经关掉，是最坏的一种误导
      alert(e instanceof Error && e.message ? e.message : '撤销失败，请稍后重试');
    }
  };

  const handleRegenerate = () => {
    revokedRef.current = false;
    setRevoked(false);
    void shareLink.refetch();
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/50">
      <div className="bg-white w-full max-w-md rounded-t-2xl sm:rounded-2xl flex flex-col overflow-hidden max-h-[90vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-7 h-7 flex items-center justify-center bg-teal-50 rounded-lg shrink-0">
              <i className="ri-share-line text-teal-600 text-sm" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900">分享订单核对单</p>
              <p className="text-xs text-gray-400 truncate">{customerName || '当前客户'}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer transition-colors rounded-lg hover:bg-gray-50 shrink-0"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="px-5 pt-4 shrink-0">
          <div className="bg-gray-50 rounded-xl p-3.5 border border-gray-100 text-xs text-gray-600 leading-relaxed space-y-1">
            <p className="flex items-start gap-1.5">
              <i className="ri-checkbox-circle-line text-gray-400 mt-0.5 shrink-0" />
              <span>只包含未付清和未付款的订单</span>
            </p>
            <p className="flex items-start gap-1.5">
              <i className="ri-refresh-line text-gray-400 mt-0.5 shrink-0" />
              <span>不存快照：打开那一刻按当前账户可见的已通过订单实时取数</span>
            </p>
            <p className="flex items-start gap-1.5">
              <i className="ri-eye-off-line text-gray-400 mt-0.5 shrink-0" />
              <span>不含审核留言、支付凭证与客户档案的其他字段</span>
            </p>
          </div>
        </div>

        <div className="px-5 pt-3 pb-5 shrink-0">
          {shareLink.isPending && (
            <div className="flex items-center gap-2 text-xs text-gray-400 py-3">
              <i className="ri-loader-4-line animate-spin" />
              正在生成核对链接…
            </div>
          )}

          {shareLink.isError && (
            <div className="bg-rose-50 border border-rose-100 rounded-xl p-3 flex items-start justify-between gap-3">
              <p className="text-xs text-rose-600 leading-relaxed">
                {shareLink.error instanceof Error && shareLink.error.message
                  ? shareLink.error.message
                  : '核对链接生成失败'}
              </p>
              <button
                onClick={handleRegenerate}
                className="text-xs px-2.5 py-1 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-100 cursor-pointer whitespace-nowrap shrink-0"
              >
                重试
              </button>
            </div>
          )}

          {share && !shareLink.isPending && (
            <div
              className={`rounded-xl border p-3 ${
                revoked ? 'bg-gray-50 border-gray-200' : 'bg-teal-50/50 border-teal-100'
              }`}
            >
              <p className="text-xs text-gray-400 mb-1.5 flex items-center gap-1">
                <i className="ri-link text-xs" />
                核对链接
                {revoked && <span className="text-rose-500">（已撤销，打开会提示链接无效）</span>}
              </p>
              <p className={`text-xs break-all leading-relaxed ${revoked ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
                {share.shareUrl}
              </p>
              <div className="flex items-center justify-between gap-2 mt-2.5">
                <span className="text-[11px] text-gray-400">
                  {new Date(share.expiresAt).toLocaleString('zh-CN')} 后自动失效
                </span>
                {revoked ? (
                  <button
                    onClick={handleRegenerate}
                    className="text-xs px-2.5 py-1 rounded-lg border border-gray-200 text-gray-600 hover:bg-white cursor-pointer whitespace-nowrap"
                  >
                    重新生成
                  </button>
                ) : (
                  <div className="flex items-center gap-2 shrink-0">
                    <a
                      href={share.shareUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs px-2.5 py-1 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                    >
                      预览
                    </a>
                    <button
                      onClick={handleRevoke}
                      disabled={revokeShare.isPending}
                      className="text-xs px-2.5 py-1 rounded-lg border border-gray-200 text-gray-500 hover:bg-white cursor-pointer whitespace-nowrap disabled:opacity-50"
                    >
                      撤销
                    </button>
                    <button
                      onClick={handleCopy}
                      className={`text-xs px-2.5 py-1 rounded-lg cursor-pointer whitespace-nowrap ${
                        copied
                          ? 'bg-teal-500 text-white'
                          : 'bg-white border border-teal-200 text-teal-600 hover:bg-teal-50'
                      }`}
                    >
                      {copied ? '已复制' : '复制链接'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          <p className="text-xs text-center text-gray-400 mt-3">
            链接可直接发到微信；收件人无需注册账号即可打开
          </p>
        </div>
      </div>
    </div>
  );
}
